import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, XCircle } from "lucide-react";
import { T } from "@/lib/testIds";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { refresh } = useAuth();

  const [state, setState] = useState({ status: "loading", info: null, error: "" });
  const [form, setForm] = useState({ name: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ status: "error", error: "Missing invite token", info: null });
      return;
    }
    (async () => {
      try {
        const r = await api.get("/team/invites/lookup", { params: { token } });
        setState({ status: "ready", info: r.data, error: "" });
      } catch (e) {
        setState({
          status: "error",
          info: null,
          error: e?.response?.data?.detail || "Invite is invalid or expired",
        });
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/team/invites/accept", { token, ...form });
      localStorage.setItem("co_token", r.data.token);
      await refresh();
      toast.success(`Welcome to ${r.data.workspace.name}`);
      nav("/app/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Accept failed");
    } finally {
      setBusy(false);
    }
  };

  if (state.status === "loading") {
    return (
      <div className="min-h-screen grid place-items-center text-sm text-[#52525B]">
        Verifying invite…
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="min-h-screen grid place-items-center px-6">
        <div className="max-w-md w-full border co-hairline bg-white p-8 text-center">
          <XCircle className="h-10 w-10 mx-auto text-[#B91C1C]" strokeWidth={1.5} aria-hidden="true" />
          <h1 className="font-heading text-2xl font-bold tracking-tight mt-4">
            Invite unavailable
          </h1>
          <p className="text-sm text-[#52525B] mt-2">{state.error}</p>
          <Link
            to="/login"
            className="mt-6 inline-block text-sm font-semibold text-[#4338CA] hover:text-[#3730A3]"
          >
            Back to sign in →
          </Link>
        </div>
      </div>
    );
  }

  const { info } = state;

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1556761175-4b46a572b786?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjbGVhbiUyMG9mZmljZSUyMHdvcmtzcGFjZXxlbnwwfHx8fDE3ODU1Njc4MDZ8MA&ixlib=rb-4.1.0&q=85"
          alt="workspace"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="co-label" style={{ color: "rgba(255,255,255,0.75)" }}>
            Team invite
          </div>
          <div className="font-heading text-3xl font-bold tracking-tight mt-3 max-w-md">
            Join {info.workspace_name} on CollectOne.
          </div>
        </div>
      </div>
      <div className="flex flex-col justify-center px-6 sm:px-12 py-12">
        <div className="max-w-md w-full">
          <Link to="/" className="inline-flex items-center gap-2 mb-10">
            <img src="/collectone-logo.webp" alt="CollectOne" className="w-8 h-8 object-contain" />
            <span className="font-heading font-bold tracking-tight">CollectOne</span>
          </Link>
          <div className="flex items-center gap-2 text-[#15803D] text-sm mb-2">
            <CheckCircle2 className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
            Invite verified
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
            Accept invite
          </h1>
          <p className="mt-2 text-sm text-[#52525B]">
            {info.invited_by_name} invited you to <span className="font-semibold">{info.workspace_name}</span>{" "}
            as <span className="font-semibold">{info.role}</span>.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label className="co-label">Email</Label>
              <Input value={info.email} disabled className="mt-2 h-11 bg-[#FAFAFA]" />
            </div>
            <div>
              <Label className="co-label">Your name</Label>
              <Input
                data-testid={T.acceptName}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="mt-2 h-11"
                placeholder="Priya Rao"
              />
            </div>
            <div>
              <Label className="co-label">Choose a password</Label>
              <Input
                data-testid={T.acceptPassword}
                type="password"
                minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                className="mt-2 h-11"
                placeholder="At least 6 characters"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid={T.acceptSubmit}
              className="w-full bg-[#4338CA] hover:bg-[#3730A3] text-white font-semibold py-3 rounded-sm co-btn-press transition-colors duration-200 disabled:opacity-60"
            >
              {busy ? "Joining…" : "Join workspace"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
